/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: GatewayFormView.tsx
 ??? Created: 2025-01-10
 ?? Description:
    Form view for creating and editing SIP Gateways with comprehensive
    configuration options organized in tabs.

 ?? Notes:
    - Uses tabs structure similar to Gateway SIP Profiles
    - Implements 7 categories with 40+ configuration fields
    - Supports Gateway Profile inheritance for optional fields
    - Includes search and Quick List functionality
    - 4-column grid layout for consistency

 ?? Last Updated: 2025-01-11 by Rodrigo Cuadra
----------------------------------------------------------------*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Phone, Search, RefreshCw, Music, MapPin, Lock, Copy, X, Sparkles, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useDebugMode } from '../../hooks/useDebugMode';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { CodecField } from '../../components/ui/CodecField';
import { SecretInput } from '../../components/ui/SecretField';
import { ModuleFormHeader } from '../../components/layouts';
import GatewaySipProfileSelect from '../../components/ui/GatewaySipProfileSelect';
import DialProfileSelect from '../../components/ui/DialProfileSelect';
import { FormBox } from '../../components/ui/FormBox';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Label } from '../../components/ui/label';
import GatewayWizardModal from './components/GatewayWizardModal';
import GatewayStatusCard from './components/GatewayStatusCard';
import {
  createGateway,
  updateGateway,
  fetchGateway,
  fetchSipProfiles,
  fetchGateways
} from './api/gatewayApi';
import {
  deleteGatewayById,
  navigateToGatewayDuplicate,
} from './gatewaysActions';
import {
  Gateway,
  GatewaySipProfile,
  GatewayFormState,
  FieldValue,
  GatewayFieldConfig,
  CategoryConfig
} from './types';
import {
  CATEGORIES,
  FIELD_CONFIG,
  DEFAULT_SETTINGS,
  createEmptyFormState,
  cloneFormState,
  serialiseFormState
} from './utils';
import { validateGatewayForm } from './utils/validators';


const tooltipContent = (
  t: ReturnType<typeof useI18n>['t'],
  field: GatewayFieldConfig
): string => {
  const base = t(`gateways.tooltips.${field.name}`);
  if (!field.possibleValues) {
    return base;
  }
  return `${base}\n\n${t('gateways.tooltipPossibleValuesPrefix')}: ${field.possibleValues}`;
};

import { GatewayConfigTabs } from './components/GatewayConfigTabs';

export default function GatewayFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { isActive: debugModeActive } = useDebugMode();

  const [formState, setFormState] = useState<GatewayFormState>(() => createEmptyFormState());
  const [originalState, setOriginalState] = useState<GatewayFormState>(() => createEmptyFormState());
  const [sipProfiles, setSipProfiles] = useState<GatewaySipProfile[]>([]);
  const [availableGateways, setAvailableGateways] = useState<Gateway[]>([]);
  const [activeTab, setActiveTab] = useState<string>(CATEGORIES[0].key);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Record<string, number>>({});
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListGateways, setQuickListGateways] = useState<Gateway[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Refs to prevent infinite loops
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);
  const currentIdRef = useRef<string | undefined>(undefined); // Initialize as undefined to detect first mount
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const isEditModeRef = useRef<boolean>(isEditMode);
  const duplicateIdRef = useRef<string | null>(duplicateId);

  // Update isEditMode ref when it changes
  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);
  useEffect(() => {
    duplicateIdRef.current = duplicateId;
  }, [duplicateId]);

  // Refs for stable function references
  const showErrorRef = useRef(showError);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    showErrorRef.current = showError;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, t, navigate]);

  const hasChanges = useMemo(() => (
    serialiseFormState(formState) !== serialiseFormState(originalState)
  ), [formState, originalState]);

  const updateSetting = useCallback((key: string, value: FieldValue) => {
    setFormState((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value
      }
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setFormState(createEmptyFormState());
  }, []);

  const handleGoToList = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    navigate('/pbx/calls-routing/gateways');
  }, [navigate]);

  const applyGatewayData = useCallback((gateway: Gateway) => {
    try {
      const baseSettings: Record<string, FieldValue> = {
        ...DEFAULT_SETTINGS,
        username: gateway.username ?? '',
        password: '',
        realm: gateway.realm ?? '',
        proxy: gateway.proxy ?? '',
        register: gateway.register ?? true
      };

      const legacySettings = gateway as unknown as Record<string, FieldValue | undefined>;
      const skipLegacyKeys = new Set<string>(['password']);
      Object.keys(DEFAULT_SETTINGS).forEach((key) => {
        if (skipLegacyKeys.has(key)) {
          return;
        }
        const value = legacySettings[key];
        if (value !== undefined && value !== null) {
          baseSettings[key] = value;
        }
      });

      if (Array.isArray(gateway.settings)) {
        gateway.settings.forEach((setting) => {
          baseSettings[setting.name] = setting.value ?? '';
        });
      }

      const nextState: GatewayFormState = {
        name: gateway.name ?? '',
        description: gateway.description ?? '',
        enabled: gateway.enabled ?? true,
        gatewaySipProfileId: gateway.gatewaySipProfileId ?? null,
        dialProfileId: gateway.dialProfileId ?? null,
        settings: baseSettings
      };

      // Use setTimeout to batch state updates and avoid triggering useEffect during render
      setTimeout(() => {
        if (duplicateIdRef.current) {
          const duplicatedState: GatewayFormState = {
            ...nextState,
            name: nextState.name ? `${nextState.name} (Copy)` : ''
          };
          setFormState(duplicatedState);
          setOriginalState(createEmptyFormState());
        } else {
          setFormState(nextState);
          setOriginalState(cloneFormState(nextState));
        }
      }, 0);
    } catch (error) {
      throw error;
    }
  }, []);

  // Ref for applyGatewayData to prevent loadGateway from recreating
  const applyGatewayDataRef = useRef(applyGatewayData);
  useEffect(() => {
    applyGatewayDataRef.current = applyGatewayData;
  }, [applyGatewayData]);

  const loadSipProfiles = useCallback(async () => {
    try {
      const profiles = await fetchSipProfiles();
      setSipProfiles(profiles);
    } catch (error) {
      const message = error instanceof Error ? error.message : tRef.current('gateways.loadError');
      showErrorRef.current(message);
    }
  }, []);

  const loadGateways = useCallback(async () => {
    try {
      const result = await fetchGateways(
        { page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' },
        undefined,
        undefined
      );
      setAvailableGateways(result.data);
    } catch (error) {
      const message = error instanceof Error ? error.message : tRef.current('gateways.loadError');
      showErrorRef.current(message);
    }
  }, []);

  // Initialize loadGateway function using useCallback and ref
  const loadGatewayFn = useCallback(async (loadId: string) => {
    // Prevent loading if already loading or already loaded this ID
    if (isLoadingRef.current || lastLoadedIdRef.current === loadId) {
      return;
    }

    if (!loadId || loadId === 'new') {
      setIsLoading(false);
      isLoadingRef.current = false;
      return;
    }

    setIsLoading(true);
    isLoadingRef.current = true;
    lastLoadedIdRef.current = loadId;

    try {
      const data = await fetchGateway(loadId);
      if (data) {
        applyGatewayDataRef.current(data);
      }
    } catch (error) {
      lastLoadedIdRef.current = null; // Reset on error to allow retry
      const message = error instanceof Error ? error.message : tRef.current('gateways.loadError');
      showErrorRef.current(message);
      navigateRef.current('/pbx/calls-routing/gateways');
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, []);

  const loadGatewayRef = useRef(loadGatewayFn);
  useEffect(() => {
    loadGatewayRef.current = loadGatewayFn;
  }, [loadGatewayFn]);

  useEffect(() => {
    // Only load initial data once when component mounts
    if (!hasLoadedInitialDataRef.current) {
      hasLoadedInitialDataRef.current = true;
      loadSipProfiles();
      loadGateways();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  useEffect(() => {
    // Only proceed if id actually changed
    const idChanged = currentIdRef.current !== id;

    if (!idChanged) {
      return; // No change, skip
    }

    currentIdRef.current = id;

    // Load gateway if editing
    if (id && id !== 'new' && lastLoadedIdRef.current !== id && !isLoadingRef.current) {
      // Use setTimeout to avoid calling setState during render
      setTimeout(() => {
        loadGatewayRef.current(id);
      }, 0);
    }

    // Reset loadedIdRef when id changes to 'new' or becomes undefined
    if (!id || id === 'new') {
      lastLoadedIdRef.current = null;
      if (!hasInitializedRef.current) {
        // Use setTimeout to avoid calling setState during render
        setTimeout(() => {
          const emptyState = createEmptyFormState();
          setFormState(emptyState);
          setOriginalState(createEmptyFormState());
          hasInitializedRef.current = true;
          setIsLoading(false);
          isLoadingRef.current = false;
        }, 0);
      }
    }
  }, [id]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults({});
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = CATEGORIES.reduce<Record<string, number>>((acc, category) => {
      const fields = FIELD_CONFIG[category.key] ?? [];
      acc[category.key] = fields.filter((field) => {
        if (field.hidden) return false;
        const label = t(`gateways.${field.name}`).toLowerCase();
        const fsName = field.fsName?.toLowerCase() ?? '';
        return (
          label.includes(query) ||
          field.name.toLowerCase().includes(query) ||
          fsName.includes(query)
        );
      }).length;
      return acc;
    }, {});

    setSearchResults(results);
    const firstCategoryWithResults = CATEGORIES.find((category) => results[category.key] > 0);
    if (firstCategoryWithResults) {
      setActiveTab(firstCategoryWithResults.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]); // Removed 't' from dependencies to prevent loops

  useEffect(() => {
    if (!showQuickList) {
      return;
    }

    // Load initial data only once when modal opens
    if (quickListGateways.length > 0) {
      return;
    }

    const loadQuickList = async () => {
      setIsQuickListLoading(true);
      try {
        const result = await fetchGateways(
          { page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' },
          undefined,
          undefined
        );
        setQuickListGateways(result.data);
      } catch (error) {
        const message = error instanceof Error ? error.message : t('gateways.loadError');
        showError(message);
        setQuickListGateways([]);
      } finally {
        setIsQuickListLoading(false);
      }
    };

    loadQuickList();
  }, [showQuickList, quickListGateways.length, showError, t]);

  const filteredQuickList = useMemo(() => {
    const gateways = quickListGateways.filter(g => g.id != null);
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return gateways;
    }

    return gateways.filter((gateway) =>
      gateway.name.toLowerCase().includes(query) ||
      (gateway.description || '').toLowerCase().includes(query)
    );
  }, [quickListGateways, quickListSearch]);
  useEffect(() => {
    if (!duplicateId) {
      return;
    }

    // Avoid re-loading the same gateway or overlapping loads
    if (isLoadingRef.current || lastLoadedIdRef.current === duplicateId) {
      return;
    }

    setTimeout(() => {
      if (duplicateId) {
        loadGatewayRef.current(duplicateId);
      }
    }, 0);
  }, [duplicateId]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) {
      return;
    }
    navigateToGatewayDuplicate(navigate, id);
  }, [id, isEditMode, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/calls-routing/gateways/new');
  }, [navigate]);

  const handleQuickListSelect = useCallback((gateway: Gateway) => {
    navigate(`/pbx/calls-routing/gateways/${gateway.uuid ?? gateway.id}`);
    setShowQuickList(false);
    setQuickListSearch('');
  }, [navigate]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    const trimmedName = formState.name.trim();
    if (!trimmedName) {
      newErrors.name = t('gateways.nameRequired');
    }

    const proxyValue = String(formState.settings.proxy ?? '').trim();
    if (!proxyValue) {
      newErrors.proxy = t('gateways.proxyRequired');
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formState, t]);

  const handleSubmit = useCallback(async (event?: React.FormEvent): Promise<Gateway | undefined> => {
    if (event) {
      event.preventDefault();
    }

    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showError(`${t('common.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    // hasChanges validation removed

    setIsSaving(true);
    try {
      const settingsPayload = Object.entries(formState.settings)
        .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
        .map(([name, value]) => ({ name, value: String(value) }));

      // Find the appropriate SIP profile for the gateway
      // Priority: 1) external, 2) internal, 3) first available
      let sipProfileId = 1;
      if (sipProfiles.length > 0) {
        const externalProfile = sipProfiles.find(profile => profile.name === 'external');
        if (externalProfile) {
          sipProfileId = externalProfile.id;
        } else {
          const internalProfile = sipProfiles.find(profile => profile.name === 'internal');
          sipProfileId = internalProfile ? internalProfile.id : sipProfiles[0].id;
        }
      }

      const payload = {
        sipProfileId,
        name: formState.name.trim(),
        description: formState.description.trim() || null,
        enabled: formState.enabled,
        username: String(formState.settings.username ?? ''),
        password: String(formState.settings.password ?? ''),
        realm: String(formState.settings.realm ?? ''),
        proxy: String(formState.settings.proxy ?? '').trim(),
        register: Boolean(formState.settings.register),
        registerTransport: String(formState.settings.registerTransport ?? 'udp'),
        expireSeconds: Number(formState.settings.expireSeconds ?? DEFAULT_SETTINGS.expireSeconds),
        retrySeconds: Number(formState.settings.retrySeconds ?? DEFAULT_SETTINGS.retrySeconds),
        fromUser: String(formState.settings.fromUser ?? ''),
        fromDomain: String(formState.settings.fromDomain ?? ''),
        contactParams: String(formState.settings.contactParams ?? ''),
        context: String(formState.settings.context ?? 'public'),
        settings: settingsPayload,
        gatewaySipProfileId: formState.gatewaySipProfileId,
        dialProfileId: formState.dialProfileId
      };

      let data: Gateway;
      if (isEditMode && id) {
        data = await updateGateway(id, payload);
      } else {
        data = await createGateway(payload);
      }

      showSuccess(isEditMode ? t('gateways.updateSuccess') : t('gateways.createSuccess'));

      const nextOriginalState = cloneFormState(formState);
      setOriginalState(nextOriginalState);

      // Update refs when creating new gateway
      if (!isEditMode && data?.id) {
        const newId = data.id.toString();
        lastLoadedIdRef.current = newId;
        hasInitializedRef.current = false;
        isLoadingRef.current = false;
        setIsLoading(false);
        currentIdRef.current = newId;
      }

      // Return saved data for useSaveWithAction
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : t('gateways.saveError');
      showError(message);
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [formState, hasChanges, id, isEditMode, showError, showInfo, showSuccess, sipProfiles, t]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as Gateway;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/calls-routing/gateways',
    newPath: '/pbx/calls-routing/gateways/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Gateways use ID for navigation
      return `/pbx/calls-routing/gateways/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formState
    }),
    storageKey: 'gateways:saveAndNewData'
  });

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormState(cloneFormState(originalState));
      return;
    }
    // Only reset if creating new and form was initialized
    if (hasInitializedRef.current) {
      resetToDefaults();
    }
  }, [isEditMode, originalState, resetToDefaults]);

  const handleDeleteClick = useCallback(() => {
    if (!isEditMode) {
      return;
    }
    setShowDeleteModal(true);
  }, [isEditMode]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteGatewayById(id);
      showSuccess(t('gateways.deletedSuccessfully'));
      navigate('/pbx/calls-routing/gateways');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('gateways.deleteError');
      showError(message);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showError, showSuccess, t]);

  const renderField = useCallback((field: GatewayFieldConfig) => {
    const value = formState.settings[field.name] ?? field.default ?? '';
    const tooltip = tooltipContent(t, field);

    const label = (
      <Label htmlFor={field.name}>
        <SimpleTooltip content={tooltip}>
          <span>
            {t(`gateways.${field.name}`)}{field.required ? ' *' : ''}
          </span>
        </SimpleTooltip>
        {debugModeActive && field.fsName && (
          <span className="text-xs text-muted-foreground mt-0.5 block">{field.fsName}</span>
        )}
      </Label>
    );

    // For codec fields, use full width (4 columns)
    const colSpan = field.type === 'codec' ? 'col-span-4' : '';

    switch (field.type) {
      case 'toggle':
        return (
          <>
            {label}
            <div>
              <Toggle
                value={Boolean(value)}
                onChange={(newValue) => updateSetting(field.name, newValue)}
              />
            </div>
          </>
        );
      case 'select':
        return (
          <>
            {label}
            <div className={colSpan}>
              <select
                id={field.name}
                value={String(value ?? '')}
                onChange={(event) => updateSetting(field.name, event.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
              >
                {field.name === 'failoverGatewayId' ? (
                  <>
                    <option value="">{t('gateways.noneOption')}</option>
                    {availableGateways.filter((gateway) => String(gateway.id) !== String(id)).map((gateway) => (
                      <option key={gateway.id} value={gateway.id}>
                        {gateway.name}
                      </option>
                    ))}
                  </>
                ) : (
                  field.options?.map((option) => (
                    <option key={option} value={option}>
                      {field.name === 'context' 
                        ? option 
                        : (t(`gateways.${field.name}Options.${option}`) || option.toUpperCase())}
                    </option>
                  ))
                )}
              </select>
            </div>
          </>
        );
      case 'number':
        return (
          <>
            {label}
            <div className={colSpan}>
              <input
                id={field.name}
                type="number"
                value={value as number | string}
                onChange={(event) => {
                  const parsed = event.target.value ? Number(event.target.value) : field.default ?? 0;
                  updateSetting(field.name, parsed);
                }}
                min={field.min}
                max={field.max}
                className="w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
              />
            </div>
          </>
        );
      case 'password':
        return (
          <>
            {label}
            <div className={colSpan}>
              <SecretInput
                value={String(value ?? '')}
                onChange={(newValue) => updateSetting(field.name, newValue)}
                placeholder={isEditMode ? t('gateways.leaveBlankToKeep') : 'secure_password'}
                showCopy
              />
            </div>
          </>
        );
      case 'codec':
        return (
          <>
            <div className="col-span-4">
              {label}
            </div>
            <div className="col-span-4">
              <CodecField
                value={String(value ?? '')}
                onChange={(newValue) => {
                  updateSetting(field.name, newValue);
                  if (field.videoOnly) {
                    updateSetting('allowVideo', newValue.length > 0);
                  }
                }}
                videoOnly={field.videoOnly}
                allowVideo={field.videoOnly ? true : undefined}
              />
            </div>
          </>
        );
      default:
        return (
          <>
            {label}
            <div className={colSpan}>
              <input
                id={field.name}
                type="text"
                value={String(value ?? '')}
                onChange={(event) => updateSetting(field.name, event.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
              />
            </div>
          </>
        );
    }
  }, [availableGateways, debugModeActive, formState.settings, id, isEditMode, t, updateSetting]);

  // Reset load guard on unmount - ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  const handleWizardApply = useCallback((config: Partial<GatewayFormState>) => {
    setFormState(prev => ({
      ...prev,
      name: config.name || prev.name,
      description: config.description || prev.description,
      enabled: config.enabled ?? prev.enabled,
      settings: {
        ...prev.settings,
        ...(config.settings || {})
      }
    }));
  }, []);

  const headerActions = useMemo(() => ([
    // 1. Quick Search (always visible)
    {
      icon: <Search className="h-5 w-5 text-muted-foreground" />,
      tooltip: t('gateways.actions.quickList'),
      onClick: () => setShowQuickList(true)
    },
    // 2. Duplicate (edit mode only)
    ...(isEditMode ? [{
      icon: <Copy className="h-5 w-5 text-muted-foreground" />,
      tooltip: t('gateways.actions.duplicate'),
      onClick: handleDuplicateFromForm
    }] : []),
    // 3. Add New (always visible)
    {
      icon: <Plus className="h-5 w-5 text-muted-foreground" />,
      tooltip: t('gateways.actions.add'),
      onClick: handleAddNew
    }
  ]), [handleAddNew, handleDuplicateFromForm, isEditMode, t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <form className="p-6" onSubmit={handleSubmit}>
      <ModuleFormHeader
        icon={<Phone className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('gateways.editTitle')} - ${formState.name || ''}` : t('gateways.createTitle')}
        backButtonText={t('common.list')}
        onBack={handleGoToList}
        searchInput={(
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full pl-9 pr-10 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {searchQuery && (
              <button
                type="button"
                aria-label={t('common.clear')}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setSearchQuery('');
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        actions={headerActions}
      >
        {/* Wizard Button - only show in create mode */}
        {!isEditMode && (
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/90 to-purple-500/90 text-white rounded-lg hover:from-primary hover:to-purple-500 transition-all shadow-lg hover:shadow-primary/25"
          >
            <Sparkles className="h-4 w-4" />
            {t('gateways.wizard.title') || 'Provider Wizard'}
          </button>
        )}
      </ModuleFormHeader>

      {/* Real-time status card (edit mode only) */}
      {isEditMode && id && (
        <div className="mt-10">
          <GatewayStatusCard gatewayId={id} gatewayName={formState.name} />
        </div>
      )}

      <div className="mt-8 pb-24 space-y-6">
        <FormBox
          title={t('gateways.basicInformation')}
          icon={<Phone className="h-5 w-5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
            <Label htmlFor="name">
              <SimpleTooltip content={tooltipContent(t, { name: 'name', type: 'text' })}>
                <span>
                  {t('gateways.name')} <span className="text-red-500">*</span>
                </span>
              </SimpleTooltip>
              {debugModeActive && (
                <span className="text-xs text-muted-foreground mt-0.5 block">gateway_name</span>
              )}
            </Label>
            <div>
              <input
                id="name"
                type="text"
                value={formState.name}
                onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                className="w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                placeholder="gw_provider"
              />
            </div>

            <Label htmlFor="description">
              <SimpleTooltip content={tooltipContent(t, { name: 'description', type: 'text' })}>
                <span>{t('gateways.description')}</span>
              </SimpleTooltip>
              {debugModeActive && (
                <span className="text-xs text-muted-foreground mt-0.5 block">description</span>
              )}
            </Label>
            <div>
              <input
                id="description"
                type="text"
                value={formState.description}
                onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                className="w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                placeholder={t('gateways.descriptionPlaceholder')}
              />
            </div>

            <Label htmlFor="dialProfileId">
              <SimpleTooltip content={t('gateways.tooltips.dialProfile', 'Select a Dial Profile to control call routing parameters.')}>
                <span>{t('gateways.dialProfile', 'Dial Profile')}</span>
              </SimpleTooltip>
              {debugModeActive && (
                <span className="text-xs text-muted-foreground mt-0.5 block">dial_profile_id</span>
              )}
            </Label>
            <div>
              <DialProfileSelect
                value={formState.dialProfileId}
                onChange={(value) => setFormState((prev) => ({ ...prev, dialProfileId: value }))}
                placeholder={t('gateways.selectDialProfile', 'Select Dial Profile')}
                nullLabel={t('gateways.noDialProfile', 'None (no profile)')}
                allowNull
              />
            </div>

            <Label htmlFor="gatewaySipProfileId">
              <SimpleTooltip content={tooltipContent(t, { name: 'gatewayProfile', type: 'select' })}>
                <span>{t('gateways.gatewayProfile')}</span>
              </SimpleTooltip>
              {debugModeActive && (
                <span className="text-xs text-muted-foreground mt-0.5 block">gateway_sip_profile_id</span>
              )}
            </Label>
            <div>
              <GatewaySipProfileSelect
                value={formState.gatewaySipProfileId}
                onChange={(value) => setFormState((prev) => ({ ...prev, gatewaySipProfileId: value }))}
                placeholder={t('gateways.selectGatewayProfile')}
                nullLabel={t('gateways.selectGatewayProfile')}
                allowNull
              />
            </div>

            <Label htmlFor="enabled">
              <SimpleTooltip content={tooltipContent(t, { name: 'enabled', type: 'toggle' })}>
                <span>{t('gateways.enabled')}</span>
              </SimpleTooltip>
              {debugModeActive && (
                <span className="text-xs text-muted-foreground mt-0.5 block">enabled</span>
              )}
            </Label>
            <div>
              <Toggle
                value={formState.enabled}
                onChange={(value) => setFormState((prev) => ({ ...prev, enabled: value }))}
              />
            </div>
          </div>
        </FormBox>

        <GatewayConfigTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          categories={CATEGORIES}
          fieldConfig={FIELD_CONFIG}
          searchQuery={searchQuery}
          searchResults={searchResults}
          renderField={renderField}
          t={t}
        />
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={handleDeleteClick}
        isEditMode={isEditMode}
        isSaving={isSaving}
        isLoading={isLoading}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="gateways:saveActionPreference"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('gateways.confirmDeleteTitle')}
        message={t('gateways.confirmDeleteMessage', { name: formState.name || t('gateways.deleteGateway') })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setQuickListGateways([]);
        }}
        title={t('gateways.quickListTitle')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(gateway) => handleQuickListSelect(gateway)}
        currentItemId={id}
        renderItem={(gateway, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              handleQuickListSelect(gateway);
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">{gateway.name}</div>
            {gateway.description && (
              <div className="text-sm text-muted-foreground mt-1">{gateway.description}</div>
            )}
          </button>
        )}
        searchPlaceholder={t('gateways.quickListSearchPlaceholder')}
        emptyMessage={t('gateways.quickListEmpty')}
        headerIcon={<Phone className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Gateway Wizard Modal */}
      <GatewayWizardModal
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onApply={handleWizardApply}
      />
    </form>
  );
}
